import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useCage } from '@/contexts/cage';
import { useGetHarvestReport } from '@/domain/harvestReports';
import useInterval from '@/hooks/useInterval';
import { SuspendedLazy } from '@/utils/suspended-lazy';

import { isMissingReportError } from './harvestReportErrors';
import { isValidReportDetail, mapApiResponseToDomain } from './mappers/apiToDomain';

const HarvestReportDetail = SuspendedLazy(() => import('./HarvestReportDetail'));
const REPORT_REFRESH_INTERVAL = 2000;

interface HarvestReportDetailContainerProps {
    cageId: number;
}

export default function HarvestReportDetailContainer(props: HarvestReportDetailContainerProps) {
    const { cageId } = props;
    const { t } = useTranslation('harvestReport');

    const { cage, location } = useCage();
    const { reportId } = useParams<{ reportId?: string | undefined }>();

    const reportQuery = useGetHarvestReport(cageId, Number.parseInt(reportId ?? '0'));
    const { data, isLoading, error, refetch } = reportQuery;

    const needsRefresh = !isLoading && (data?.status === 'created' || data?.status === 'started');
    const loading = isLoading || needsRefresh;

    useInterval(refetch, needsRefresh ? REPORT_REFRESH_INTERVAL : null);

    const notFound = (
        <EmptyState title={t('reportDetail.notFound')} headingLevel="h1" size="spacious" />
    );

    // Like the error state below, the blank stands in for the report and so takes its h1.
    if (isMissingReportError(error)) {
        return notFound;
    }

    // The report's title goes with the report, so the error state stands in as the page's h1.
    if (error) {
        return <SomethingHappened headingLevel="h1" {...retryFailedQueries(reportQuery)} />;
    }

    if (loading) {
        return <Spinner size="lg" fullScreen />;
    }

    if (!data) {
        return notFound;
    }

    const apiResponse = data;

    // Validate API response structure
    if (!isValidReportDetail(apiResponse)) {
        return <SomethingHappened headingLevel="h1" />;
    }

    const report = mapApiResponseToDomain(apiResponse, {
        locationName: location?.name,
        cageName: cage?.name,
    });
    return <HarvestReportDetail report={report} />;
}
